import {
  type HydrateEvidenceLocator,
  type HydrateEvidenceResponse,
} from '@/shared/mobile-service/api/cors-mobile-api';

const BASE_PATH = '/mobile-viewer';

/**
 * Deep-link query the standalone viewer understands:
 *
 * - ``row`` — cited artifact ids. Messages and the map highlight every id.
 *   Tables highlight the same set and select the first id.
 * - ``threadId`` — messages view opens that conversation.
 * - ``view`` — ``messages``, ``map``, or ``table`` when hydrate names one.
 */

/** Query-string fields a citation writes so the viewer can open on the cited rows. */
export interface MobileCitationLink {
  acquisitionId: number;
  graphqlType: string | null;
  view: string | null;
  rowIds: Array<string | number>;
  threadId: string | number | null;
}

export function citedArtifactIds(locator: HydrateEvidenceLocator): number[] {
  const ids: number[] = [];
  const seen = new Set<number>();
  const add = (value: unknown) => {
    const n = typeof value === 'number' ? value : Number.parseInt(String(value ?? ''), 10);
    if (!Number.isFinite(n) || seen.has(n)) return;
    seen.add(n);
    ids.push(n);
  };
  add(locator.mobileArtifactId);
  for (const value of (locator.mobileArtifactIds as number[] | undefined) ?? []) add(value);
  return ids;
}

/** Build the deep-link a citation click should open. */
export function mobileViewerHref(link: MobileCitationLink): string {
  const params = new URLSearchParams();
  params.set('acquisitionId', String(link.acquisitionId));
  if (link.graphqlType) params.set('type', link.graphqlType);
  if (link.view) params.set('view', link.view);
  if (link.rowIds.length) params.set('row', link.rowIds.map(String).join(','));
  if (link.threadId != null && String(link.threadId) !== '') {
    params.set('threadId', String(link.threadId));
  }
  return `${BASE_PATH}?${params.toString()}`;
}

function focusIdsFromHydrate(hydrated: HydrateEvidenceResponse): number[] {
  if (hydrated.focus?.mobileArtifactIds?.length) {
    return hydrated.focus.mobileArtifactIds;
  }
  if (hydrated.focus?.mobileArtifactId != null) {
    return [hydrated.focus.mobileArtifactId];
  }
  return citedArtifactIds(hydrated.locator);
}

/** Turn a hydrate response into the URL the message/map/table viewer understands. */
export function mobileViewerHrefFromHydrate(hydrated: HydrateEvidenceResponse): string | null {
  const locator = hydrated.locator;
  const acquisitionId = locator.mobileAcquisitionId;
  if (!Number.isFinite(acquisitionId)) return null;
  const graphqlType =
    (typeof locator.graphqlType === 'string' && locator.graphqlType) || null;
  if (!graphqlType) return null;

  const rowIds = focusIdsFromHydrate(hydrated);
  const threadId = hydrated.focus?.mobileThreadId ?? hydrated.focus?.chatArtifactId ?? null;

  return mobileViewerHref({
    acquisitionId,
    graphqlType,
    view: hydrated.view ?? (typeof locator.requestedView === 'string' ? locator.requestedView : null),
    rowIds,
    threadId,
  });
}

/** Best-effort deep-link from the skinny locator when hydrate is unavailable. */
export function mobileViewerHrefFromLocator(locator: HydrateEvidenceLocator): string | null {
  const acquisitionId = locator.mobileAcquisitionId;
  if (!Number.isFinite(acquisitionId)) return null;
  const graphqlType =
    (typeof locator.graphqlType === 'string' && locator.graphqlType) || null;
  if (!graphqlType) return null;
  const ids = citedArtifactIds(locator);
  const threadId =
    (typeof locator.mobileThreadId === 'number' && locator.mobileThreadId) ||
    (typeof locator.chatArtifactId === 'number' && locator.chatArtifactId) ||
    null;
  return mobileViewerHref({
    acquisitionId,
    graphqlType,
    view: typeof locator.requestedView === 'string' ? locator.requestedView : null,
    rowIds: ids,
    threadId,
  });
}

export function isMobileEvidenceLocator(value: unknown): value is HydrateEvidenceLocator {
  if (!value || typeof value !== 'object') return false;
  const locator = value as HydrateEvidenceLocator;
  return (
    typeof locator.kind === 'string' &&
    typeof locator.mobileAcquisitionId === 'number' &&
    Number.isFinite(locator.mobileAcquisitionId)
  );
}
